// src/components/SuperAdminReportsTab.js
// Super admin "Reports" tab — platform-wide revenue, per-mall breakdown, channel split, daily trend.
import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import DateRangePicker from './DateRangePicker';
import { downloadCSV } from '../utils/downloadCSV';

function toDateStr(d) { return d.toISOString().slice(0, 10); }
function defaultRange() {
  const end = new Date();
  const start = new Date(end); start.setDate(start.getDate() - 29);
  return { startDate: toDateStr(start), endDate: toDateStr(end) };
}
const cardStyle = { background: 'var(--bg2)', borderRadius: 10, padding: 14, minWidth: 150 };

export default function SuperAdminReportsTab() {
  const [range, setRange]     = useState(defaultRange());
  const [report, setReport]   = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  const load = useCallback(async (r) => {
    setLoading(true); setError('');
    try {
      const res = await api.get('/super-admin/reports/platform-revenue', { params: r });
      setReport(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(range); }, [load, range]);

  const totalOutstanding = report?.perMall?.reduce((s, m) => s + (m.outstandingPayouts || 0), 0) || 0;
  const mallsWithOutstanding = report?.perMall?.filter(m => m.outstandingPayouts > 0).length || 0;

  return (
    <div>
      <div className="card" style={{ marginBottom: 20 }}>
        <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 6 }}>📊 Platform Revenue Report</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', marginBottom: 16 }}>
          <DateRangePicker startDate={range.startDate} endDate={range.endDate} onChange={setRange} />
          <button
            className="btn btn-sm"
            onClick={() => downloadCSV('/super-admin/reports/platform-revenue/csv', range, `platform-revenue-${range.startDate}-to-${range.endDate}.csv`)}
          >
            📥 CSV
          </button>
        </div>

        {loading && <div style={{ color: 'var(--text2)' }}>Loading…</div>}
        {error && <div style={{ color: '#e55' }}>{error}</div>}

        {report && !loading && (
          <>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 12 }}>
              <div style={cardStyle}>
                <div style={{ fontSize: 11, color: 'var(--text2)' }}>GMV</div>
                <div style={{ fontSize: 18, fontWeight: 700 }}>AED {report.gmv.total.amount.toFixed(2)}</div>
                <div style={{ fontSize: 11, color: 'var(--text2)' }}>{report.gmv.total.orders} orders</div>
              </div>
              <div style={cardStyle}>
                <div style={{ fontSize: 11, color: 'var(--text2)' }}>Commission</div>
                <div style={{ fontSize: 18, fontWeight: 700 }}>AED {report.platformRevenue.commission.toFixed(2)}</div>
              </div>
              <div style={cardStyle}>
                <div style={{ fontSize: 11, color: 'var(--text2)' }}>Service Fees (Platform)</div>
                <div style={{ fontSize: 18, fontWeight: 700 }}>AED {report.platformRevenue.serviceFees.toFixed(2)}</div>
              </div>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 20 }}>
              <div style={cardStyle}>
                <div style={{ fontSize: 11, color: 'var(--text2)' }}>Platform Revenue</div>
                <div style={{ fontSize: 18, fontWeight: 700 }}>AED {report.platformRevenue.total.toFixed(2)}</div>
                <div style={{ fontSize: 11, color: 'var(--text2)' }}>Comm + Fees</div>
              </div>
              <div style={cardStyle}>
                <div style={{ fontSize: 11, color: 'var(--text2)' }}>Net to Vendors</div>
                <div style={{ fontSize: 18, fontWeight: 700 }}>AED {report.netToVendors.toFixed(2)}</div>
              </div>
              <div style={cardStyle}>
                <div style={{ fontSize: 11, color: 'var(--text2)' }}>Outstanding Payouts</div>
                <div style={{ fontSize: 18, fontWeight: 700 }}>AED {totalOutstanding.toFixed(2)}</div>
                <div style={{ fontSize: 11, color: 'var(--text2)' }}>{mallsWithOutstanding} malls</div>
              </div>
            </div>
          </>
        )}
      </div>

      {report && !loading && (
        <>
          <div className="card" style={{ marginBottom: 20 }}>
            <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8 }}>Per-Mall Breakdown</div>
            <div style={{ overflowX: 'auto' }}>
              <table className="table" style={{ width: '100%', fontSize: 13 }}>
                <thead>
                  <tr>
                    <th>Mall</th><th>GMV</th><th>Online</th><th>Cash</th>
                    <th>Commission</th><th>Service Fees</th><th>Net to Vendors</th><th>Outstanding</th>
                  </tr>
                </thead>
                <tbody>
                  {report.perMall.map(m => (
                    <tr key={m.mallId}>
                      <td>{m.mallName}</td>
                      <td>{m.gmv.total.amount.toFixed(2)}</td>
                      <td>{m.gmv.online.amount.toFixed(2)}</td>
                      <td>{m.gmv.cash.amount.toFixed(2)}</td>
                      <td>{m.commission.toFixed(2)}</td>
                      <td>{m.serviceFees.total.toFixed(2)}</td>
                      <td>{m.netToVendors.toFixed(2)}</td>
                      <td>{m.outstandingPayouts.toFixed(2)}</td>
                    </tr>
                  ))}
                  {report.directVendors && (
                    <tr>
                      <td>Direct Vendors</td>
                      <td>{report.directVendors.gmv.total.amount.toFixed(2)}</td>
                      <td>{report.directVendors.gmv.online.amount.toFixed(2)}</td>
                      <td>{report.directVendors.gmv.cash.amount.toFixed(2)}</td>
                      <td>{report.directVendors.commission.toFixed(2)}</td>
                      <td>{report.directVendors.serviceFees.total.toFixed(2)}</td>
                      <td>{report.directVendors.netToVendors.toFixed(2)}</td>
                      <td>0.00</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="card" style={{ marginBottom: 20 }}>
            <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8 }}>Payment Channel Breakdown</div>
            <ChannelBar label="Online" amount={report.gmv.online.amount} total={report.gmv.total.amount} color="var(--accent)" />
            <ChannelBar label="Cash" amount={report.gmv.cash.amount} total={report.gmv.total.amount} color="var(--text2)" />
          </div>

          <div className="card">
            <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8 }}>Daily Revenue Trend</div>
            <div style={{ overflowX: 'auto' }}>
              <table className="table" style={{ width: '100%', fontSize: 13 }}>
                <thead>
                  <tr><th>Date</th><th>GMV</th><th>Commission</th><th>Service Fees</th><th>Platform Revenue</th></tr>
                </thead>
                <tbody>
                  {report.dailyTrend.map(d => (
                    <tr key={d.date}>
                      <td>{d.date}</td>
                      <td>{d.gmv.toFixed(2)}</td>
                      <td>{d.commission.toFixed(2)}</td>
                      <td>{d.serviceFees.toFixed(2)}</td>
                      <td>{d.platformRevenue.toFixed(2)}</td>
                    </tr>
                  ))}
                  {report.dailyTrend.length === 0 && (
                    <tr><td colSpan={5} style={{ textAlign: 'center', color: 'var(--text2)' }}>No activity in this period</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function ChannelBar({ label, amount, total, color }) {
  const pct = total > 0 ? (amount / total) * 100 : 0;
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
        <span>{label}</span>
        <span>AED {amount.toFixed(2)} ({pct.toFixed(1)}%)</span>
      </div>
      <div style={{ background: 'var(--bg3)', borderRadius: 6, height: 8, overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, background: color, height: '100%' }} />
      </div>
    </div>
  );
}
